import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Utensils, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { formatDDMMYYYY } from '../../utils/date';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const COMIDAS = ['desayuno', 'almuerzo', 'cena', 'colacion'];

export default function SgCasino() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rut, setRut] = useState('');
  const [verif, setVerif] = useState(null);
  const [comida, setComida] = useState('almuerzo');
  const [obs, setObs] = useState('');
  const [saving, setSaving] = useState(false);
  const [usos, setUsos] = useState([]);

  const verificar = async () => {
    if (!rut.trim()) return;
    setVerif(null);
    try {
      const r = await axios.get(`${API}/servicios-generales/verificar-habilitacion`, { params: { rut } });
      setVerif(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const registrar = async () => {
    if (!verif?.casino_habilitado) return;
    setSaving(true);
    try {
      await axios.post(`${API}/servicios-generales/casino/usos`, { rut_trabajador: rut, comida, observaciones: obs }, { headers });
      toast.success(`Registro creado para ${verif.nombre}`);
      setRut(''); setVerif(null); setObs(''); loadHoy();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const loadHoy = async () => {
    try {
      const r = await axios.get(`${API}/servicios-generales/casino/usos`, { headers });
      setUsos(r.data);
    } catch { }
  };
  React.useEffect(() => { loadHoy(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Casino</h1>
        <p className="text-sm text-slate-500">Registro de uso del casino por RUT.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Utensils className="w-4 h-4 text-emerald-700" />Nuevo registro</h3>
          <div className="space-y-3">
            <div className="flex gap-2">
              <input value={rut} onChange={e => setRut(e.target.value)} onKeyDown={e => e.key === 'Enter' && verificar()}
                placeholder="RUT del trabajador (ej. 12.345.678-9)" className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="casino-rut" />
              <button onClick={verificar} className="px-4 py-2 bg-slate-800 text-white text-sm rounded-lg" data-testid="casino-verificar"><Search className="w-4 h-4" /></button>
            </div>

            {verif && (
              <div className={`p-3 rounded-lg border ${verif.casino_habilitado ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                {verif.encontrado ? (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      {verif.casino_habilitado ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                      <p className="font-bold text-slate-900">{verif.nombre}</p>
                    </div>
                    <p className="text-xs text-slate-600">RUT {verif.rut} · {verif.empresa_nombre}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{verif.motivo}</p>
                  </>
                ) : <p className="text-sm text-red-700">Trabajador no encontrado.</p>}
              </div>
            )}

            {verif?.casino_habilitado && (
              <>
                <div>
                  <label className="text-xs text-slate-500 font-semibold">Comida</label>
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {COMIDAS.map(c => (
                      <button key={c} onClick={() => setComida(c)}
                        className={`px-2 py-2 text-xs font-semibold rounded-lg border capitalize ${comida === c ? 'bg-orange-600 text-white border-emerald-700' : 'border-slate-200 hover:bg-slate-50'}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-semibold">Observaciones</label>
                  <input value={obs} onChange={e => setObs(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
                </div>
                <button onClick={registrar} disabled={saving}
                  className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg disabled:opacity-50"
                  data-testid="casino-registrar">
                  {saving ? 'Registrando...' : 'Registrar uso de casino'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-500" />Usos de hoy ({usos.length})</h3>
          <div className="max-h-[500px] overflow-y-auto space-y-2">
            {usos.length === 0 ? <p className="text-sm text-slate-400">Sin registros aún.</p> :
              usos.map(u => (
                <div key={u.id} className="p-2 border border-slate-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800">{u.nombre_trabajador}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 uppercase font-bold">{u.comida}</span>
                  </div>
                  <p className="text-[11px] text-slate-500">{u.rut_trabajador} · {u.empresa_nombre} · {formatDDMMYYYY(u.fecha)} {u.hora?.slice(0, 5)}</p>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
